import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Crown, Check, Phone, Calendar, Shield, Clock, Wrench, DollarSign } from 'lucide-react';
import { generateLoyaltyProgramSchema, generateBreadcrumbSchema } from '../utils/schemaMarkup';
import { useSchemaMarkup } from '../utils/useSchemaMarkup';

const benefits = [
  {
    icon: Wrench,
    title: 'Seasonal Tune-Ups',
    description: 'Two comprehensive tune-ups per year (heating & cooling)'
  },
  {
    icon: Clock,
    title: 'Priority Scheduling',
    description: 'Skip the line with priority service scheduling'
  },
  {
    icon: DollarSign,
    title: '10% Repair Discount',
    description: 'Save 10% on all repair services'
  },
  {
    icon: Shield,
    title: 'No Overtime Charges',
    description: 'Same rates regardless of time or day'
  }
];

const includedServices = [
  'Fall furnace tune-up',
  'Fall furnace tune-up',
  'Priority emergency service',
  '10% discount on repairs',
  'Filter replacement reminders',
  'Extended warranty options',
  'Annual system report'
];

const ComfortLoyaltyPage = () => {
  // Generate schemas
  const loyaltySchema = generateLoyaltyProgramSchema();
  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: 'https://comfortmasterheating.com' },
    { name: 'Comfort Loyalty', url: 'https://comfortmasterheating.com/comfort-loyalty' }
  ]);

  // Inject schemas into document head
  useSchemaMarkup([loyaltySchema, breadcrumbSchema], 'comfort-loyalty');

  return (
    <main>
      <Helmet>
        <title>ComfortLoyalty Club | $16/Month HVAC Maintenance Plan | ComfortMaster</title>
        <meta name="description" content="Join ComfortLoyalty Club for $16/month. Get seasonal tune-ups, priority scheduling, 10% repair discount, and no overtime charges. Best HVAC maintenance plan in Kentucky." />
        <link rel="canonical" href="https://comfortmasterheating.com/comfort-loyalty" />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-emerald-600 to-emerald-700 text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'}}></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-yellow-500 text-slate-900 px-4 py-2 rounded-full text-sm font-bold mb-6">
              <Crown size={18} />
              <span>PREMIUM MEMBERSHIP</span>
            </div>
            <h1 className="font-heading font-bold text-5xl md:text-6xl mb-6">
              ComfortLoyalty Club
            </h1>
            <p className="text-xl text-emerald-100 max-w-2xl mx-auto mb-4">
              Join for only <span className="font-bold text-white">$16 per month</span>
            </p>
            <p className="text-lg text-emerald-100 max-w-2xl mx-auto mb-8">
              Seasonal tune-ups, priority service & money saving benefits that will make your equipment last longer and keep you (and your home) happier.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/comfort-loyalty/sign-up" className="inline-flex items-center justify-center gap-2 bg-white text-emerald-700 font-bold py-4 px-8 rounded-full shadow-lg hover:shadow-xl transition-all" data-testid="loyalty-signup-hero-btn">
                <Crown size={20} />
                Join Now - $16/month
              </Link>
              <a href="tel:8592550728" className="btn-secondary border-white/30 text-white hover:bg-white hover:text-emerald-700" data-testid="loyalty-call-btn">
                <Phone size={20} className="inline mr-2" />
                (859) 255-0728
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-20 bg-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading font-bold text-4xl text-slate-900 mb-4">Member Benefits</h2>
            <p className="text-xl text-slate-600">Everything you get with your ComfortLoyalty membership</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white p-6 rounded-2xl shadow-sm text-center" data-testid={`benefit-${index}`}>
                <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="font-heading font-bold text-lg text-slate-900 mb-2">{benefit.title}</h3>
                <p className="text-slate-600 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-sm">
              <h3 className="font-heading font-bold text-2xl text-slate-900 mb-6">What's Included</h3>
              <ul className="space-y-3">
                {includedServices.map((service, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center">
                      <Check size={14} className="text-emerald-600" />
                    </div>
                    <span className="text-slate-700">{service}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 p-8 rounded-2xl text-white">
              <div className="text-center">
                <Crown className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
                <h3 className="font-heading font-bold text-3xl mb-2">ComfortLoyalty</h3>
                <p className="text-emerald-100 mb-6">Premium Monthly Membership</p>
                <div className="bg-white/10 rounded-xl p-6 mb-6">
                  <p className="text-5xl font-heading font-bold">$16</p>
                  <p className="text-emerald-200">per month</p>
                </div>
                <Link to="/comfort-loyalty/sign-up" className="block w-full bg-yellow-500 text-slate-900 font-bold py-4 px-8 rounded-full hover:bg-yellow-400 transition-colors" data-testid="loyalty-signup-card-btn">
                  Sign Up Now
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ComfortLoyaltyPage;
